/**
 * Checklist Management Functions
 * Handles checklist CRUD operations and UI
 */

// Import i18n system
import { t } from '../i18n.js';
import { escapeHtml } from '../utils.js';

// Refresh Lucide icons after DOM changes
function refreshIcons() {
  if (typeof lucide !== 'undefined') {
    lucide.createIcons()
  }
}

// ==================== CHECKLIST UI ====================

/**
 * Load and display all checklists for current tenant
 */
export async function loadChecklists() {
  const container = document.getElementById('checklistsList')
  if (!container) return
  
  container.innerHTML = `<p class="loading">${t('checklist.loading')}</p>`
  
  try {
    // Import dynamically to avoid circular dependency
    const { supabase, currentUser } = await import('./main.js')
    
    const { data: checklists, error } = await supabase
      .from('checklists')
      .select('*')
      .eq('tenant_id', currentUser.tenant_id)
      .eq('is_active', true)
      .order('name', { ascending: true })
    
    if (error) throw error
    
    if (!checklists || checklists.length === 0) {
      container.innerHTML = `
        <div class="empty-state">
          <div class="empty-icon"><i data-lucide="clipboard-check"></i></div>
          <h3>${t('checklist.emptyTitle')}</h3>
          <p>${t('checklist.emptyDescription')}</p>
          <button id="createFirstChecklistBtn" class="btn btn-primary">
            <i data-lucide="plus"></i> ${t('checklist.createBtn')}
          </button>
        </div>
      `
      
      document.getElementById('createFirstChecklistBtn')?.addEventListener('click', () => {
        openChecklistModal()
      })
      refreshIcons()
      return
    }
    
    // Count equipment using each checklist
    const { data: equipmentCounts } = await supabase
      .from('equipment')
      .select('checklist_id')
      .eq('tenant_id', currentUser.tenant_id)
    
    const counts = {}
    equipmentCounts?.forEach(eq => {
      if (eq.checklist_id) {
        counts[eq.checklist_id] = (counts[eq.checklist_id] || 0) + 1
      }
    })
    
    // Render checklists
    let html = '<div class="checklists-grid">'
    
    checklists.forEach(checklist => {
      const itemCount = checklist.sections?.length || 0
      const usageCount = counts[checklist.id] || 0
      
      html += `
        <div class="checklist-card">
          <div class="checklist-card-header">
            <h3><i data-lucide="clipboard-check"></i> ${escapeHtml(checklist.name)}</h3>
            <div class="checklist-card-actions">
              <button 
                class="btn-icon" 
                onclick="editChecklist('${checklist.id}')"
                title="${t('common.edit')}"
              >
                <i data-lucide="edit"></i>
              </button>
              <button 
                class="btn-icon" 
                onclick="deleteChecklist('${checklist.id}')"
                title="${t('common.delete')}"
              >
                <i data-lucide="trash-2"></i>
              </button>
            </div>
          </div>
          
          ${checklist.description ? `
            <p class="checklist-description">${escapeHtml(checklist.description)}</p>
          ` : ''}
          
          <div class="checklist-stats">
            <div class="checklist-stat">
              <span class="stat-value">${itemCount}</span>
              <span class="stat-label">${t('checklist.statsItems')}</span>
            </div>
            <div class="checklist-stat">
              <span class="stat-value">${usageCount}</span>
              <span class="stat-label">${t('checklist.statsEquipment')}</span>
            </div>
          </div>
          
          <div class="checklist-preview">
            ${checklist.sections?.slice(0, 3).map(item => `
              <div class="checklist-preview-item"><i data-lucide="square"></i> ${escapeHtml(item.title)}</div>
            `).join('') || ''}
            ${itemCount > 3 ? `<div class="checklist-preview-more">${t('checklist.moreItems').replace('{count}', itemCount - 3)}</div>` : ''}
          </div>
        </div>
      `
    })
    
    html += '</div>'
    container.innerHTML = html
    refreshIcons()
    
    // Update stats
    const statsEl = document.getElementById('totalChecklists')
    if (statsEl) {
      statsEl.textContent = checklists.length
    }
    
  } catch (error) {
    console.error('Error loading checklists:', error)
    container.innerHTML = `<p class="error-message">${t('checklist.loadingError')}</p>`
  }
}

/**
 * Open checklist modal (create or edit)
 */
export function openChecklistModal(checklistId = null) {
  const modal = document.getElementById('checklistModal')
  const form = document.getElementById('checklistForm')
  const title = document.getElementById('checklistModalTitle')

  if (!modal || !form || !title) {
    console.error('Checklist modal elements not found!')
    return
  }

  // Re-bind handlers each time the modal opens (safe: onclick replaces, never stacks)
  initEventListeners()

  modal.style.display = 'flex'
  form.reset()
  
  if (checklistId) {
    title.textContent = t('checklist.editTitle')
    document.getElementById('checklistId').value = checklistId
    loadChecklistData(checklistId)
  } else {
    title.textContent = t('checklist.createTitle')
    document.getElementById('checklistId').value = ''
    renderChecklistItems([])
  }
}

/**
 * Load checklist data for editing
 */
async function loadChecklistData(checklistId) {
  try {
    const { supabase } = await import('./main.js')
    
    const { data: checklist, error } = await supabase
      .from('checklists')
      .select('*')
      .eq('id', checklistId)
      .single()
    
    if (error) throw error
    
    document.getElementById('checklistName').value = checklist.name || ''
    document.getElementById('checklistDescription').value = checklist.description || ''
    
    renderChecklistItems(checklist.sections || [])
    
  } catch (error) {
    console.error('Error loading checklist:', error)
    alert(t('error.loadingChecklistData'))
  }
}

/**
 * Render checklist items in edit form
 */
function renderChecklistItems(items = []) {
  const container = document.getElementById('checklistItemsList')
  
  if (!container) return
  
  container.innerHTML = ''
  
  if (items.length === 0) {
    // Add first empty item
    addChecklistItemRow()
  } else {
    items.forEach((item, index) => {
      addChecklistItemRow(item.title, index)
    })
  }
}

/**
 * Add a checklist item row
 */
function addChecklistItemRow(value = '', index = null) {
  const container = document.getElementById('checklistItemsList')
  if (!container) return
  
  const itemIndex = index !== null ? index : container.children.length
  
  const div = document.createElement('div')
  div.className = 'checklist-item-row'
  div.dataset.index = itemIndex
  
  div.innerHTML = `
    <span class="item-number">${itemIndex + 1}.</span>
    <input 
      type="text" 
      class="checklist-item-input" 
      placeholder="${t('checklist.itemPlaceholder')}"
      value="${escapeHtml(value)}"
      required
    >
    <button 
      type="button" 
      class="btn-icon btn-remove-item" 
      onclick="removeChecklistItem(this)"
      title="${t('common.remove')}"
    >
      ×
    </button>
  `
  
  container.appendChild(div)
}

/**
 * Remove checklist item row
 */
window.removeChecklistItem = function(button) {
  const row = button.closest('.checklist-item-row')
  if (row) {
    row.remove()
    renumberChecklistItems()
  }
}

/**
 * Renumber checklist items after deletion
 */
function renumberChecklistItems() {
  const rows = document.querySelectorAll('.checklist-item-row')
  rows.forEach((row, index) => {
    row.dataset.index = index
    row.querySelector('.item-number').textContent = `${index + 1}.`
  })
}

// ==================== CHECKLIST OPERATIONS ====================

/**
 * Save checklist (create or update)
 */
export async function saveChecklist(e) {
  e.preventDefault()
  
  console.log('=== CHECKLIST SAVE DEBUG ===')
  console.log('Form element:', e.target)
  console.log('checklistId:', document.getElementById('checklistId'))
  console.log('checklistName:', document.getElementById('checklistName'))
  console.log('checklistDescription:', document.getElementById('checklistDescription'))
  
  const checklistIdEl = document.getElementById('checklistId')
  const nameEl = document.getElementById('checklistName')
  const descEl = document.getElementById('checklistDescription')
  
  if (!nameEl) {
    alert(t('error.checklistNameElementNotFound'))
    console.error('Modal did not open correctly - form elements missing')
    return
  }
  
  const checklistId = checklistIdEl ? checklistIdEl.value : ''
  const name = nameEl.value ? nameEl.value.trim() : ''
  const description = descEl && descEl.value ? descEl.value.trim() : ''
  
  console.log('Extracted values:', { checklistId, name, description })
  
  if (!name) {
    alert(t('alert.enterChecklistName'))
    return
  }
  
  // Collect items
  const inputs = document.querySelectorAll('.checklist-item-input')
  const sections = []
  
  inputs.forEach((input, i) => {
    const title = input.value ? input.value.trim() : ''
    if (title) {
      sections.push({
        id: `${i + 1}`,
        title: title,
        required: true
      })
    }
  })
  
  if (sections.length === 0) {
    alert(t('alert.addAtLeastOneItem'))
    return
  }
  
  const saveBtn = document.getElementById('saveChecklistBtnText')
  const submitBtn = e.target.querySelector('button[type="submit"]')
  
  if (saveBtn) saveBtn.textContent = t('common.saving')
  if (submitBtn) submitBtn.disabled = true
  
  try {
    const { supabase, currentUser } = await import('./main.js')
    
    const checklistData = {
      tenant_id: currentUser.tenant_id,
      name: name,
      description: description || null,
      sections: sections,
      is_active: true,
      updated_at: new Date().toISOString()
    }
    
    if (checklistId) {
      // Update existing
      const { error } = await supabase
        .from('checklists')
        .update(checklistData)
        .eq('id', checklistId)
      
      if (error) throw error
      
      alert(t('alert.checklistUpdated'))
    } else {
      // Create new
      checklistData.created_at = new Date().toISOString()
      
      const { error } = await supabase
        .from('checklists')
        .insert([checklistData])
      
      if (error) throw error
      
      alert(t('alert.checklistCreated'))
    }
    
    closeChecklistModal()
    await loadChecklists()
    await loadChecklistsForDropdowns()
    
  } catch (error) {
    console.error('Error saving checklist:', error)
    alert(t('error.savingChecklist') + error.message)
  } finally {
    if (saveBtn) saveBtn.textContent = t('checklist.saveBtn')
    if (submitBtn) submitBtn.disabled = false
  }
}

/**
 * Delete checklist
 */
export async function deleteChecklist(checklistId) {
  try {
    const { supabase } = await import('./main.js')
    
    // Check if checklist is in use
    const { data: equipment, error: countError } = await supabase
      .from('equipment')
      .select('id')
      .eq('checklist_id', checklistId)
    
    if (countError) throw countError
    
    const usageCount = equipment?.length || 0
    
    let confirmMsg = t('confirm.deleteChecklist')
    if (usageCount > 0) {
      confirmMsg = t('confirm.deleteChecklistInUse')
        .replace('{count}', usageCount)
        .replace('{plural}', usageCount > 1 ? 's' : '')
    }
    
    if (!confirm(confirmMsg)) return
    
    // Soft delete (set is_active = false)
    const { error } = await supabase
      .from('checklists')
      .update({ is_active: false })
      .eq('id', checklistId)
    
    if (error) throw error
    
    alert(t('alert.checklistDeleted'))
    await loadChecklists()
    await loadChecklistsForDropdowns()
    
  } catch (error) {
    console.error('Error deleting checklist:', error)
    alert(t('error.deletingChecklist') + error.message)
  }
}

/**
 * Edit checklist
 */
window.editChecklist = function(checklistId) {
  openChecklistModal(checklistId)
}

/**
 * Delete checklist (global wrapper)
 */
window.deleteChecklist = function(checklistId) {
  deleteChecklist(checklistId)
}

/**
 * Close checklist modal
 */
export function closeChecklistModal() {
  const modal = document.getElementById('checklistModal')
  if (modal) {
    modal.style.display = 'none'
  }
}

// ==================== CHECKLIST DROPDOWNS ====================

/**
 * Load checklists for dropdowns (in equipment forms)
 */
export async function loadChecklistsForDropdowns() {
  try {
    const { supabase, currentUser } = await import('./main.js')
    
    const { data: checklists, error } = await supabase
      .from('checklists')
      .select('id, name')
      .eq('tenant_id', currentUser.tenant_id)
      .eq('is_active', true)
      .order('name', { ascending: true })
    
    if (error) throw error
    
    // Update all checklist dropdowns
    const dropdowns = [
      document.getElementById('checklistSelect'),
      document.getElementById('editChecklistSelect')
    ]
    
    dropdowns.forEach(dropdown => {
      if (!dropdown) return
      
      const currentValue = dropdown.value
      
      dropdown.innerHTML = `<option value="">${t('checklist.noChecklistOption')}</option>`
      
      checklists?.forEach(checklist => {
        const option = document.createElement('option')
        option.value = checklist.id
        option.textContent = checklist.name
        dropdown.appendChild(option)
      })
      
      // Restore previous selection
      if (currentValue) {
        dropdown.value = currentValue
      }
    })
    
  } catch (error) {
    console.error('Error loading checklists for dropdowns:', error)
  }
}

// ==================== CHECKLIST IN WORK LOG ====================

/**
 * Load checklist for equipment in work log modal
 */
export async function loadChecklistForWorkLog(equipmentId) {
  const checklistSection = document.getElementById('checklistSection')
  
  if (!checklistSection) return
  
  try {
    const { supabase } = await import('./main.js')
    
    // Get equipment with checklist
    const { data: equipment, error } = await supabase
      .from('equipment')
      .select(`
        checklist_id,
        checklist:checklists (
          id,
          name,
          sections
        )
      `)
      .eq('id', equipmentId)
      .single()
    
    if (error) throw error
    
    if (!equipment.checklist_id || !equipment.checklist) {
      checklistSection.style.display = 'none'
      return
    }
    
    // Display checklist
    const checklist = equipment.checklist
    const nameEl = document.getElementById('workLogChecklistName')
    if (nameEl) {
      nameEl.textContent = checklist.name
    }
    
    const itemsContainer = document.getElementById('checklistItems')
    if (itemsContainer) {
      itemsContainer.innerHTML = ''
      
      checklist.sections?.forEach(item => {
        const div = document.createElement('div')
        div.className = 'checklist-item'
        div.innerHTML = `
          <label class="checkbox-label">
            <input 
              type="checkbox" 
              class="checklist-checkbox" 
              data-item-id="${item.id}"
            >
            <span>${escapeHtml(item.title)}</span>
          </label>
        `
        itemsContainer.appendChild(div)
      })
    }
    
    checklistSection.style.display = 'block'
    
  } catch (error) {
    console.error('Error loading checklist for work log:', error)
    checklistSection.style.display = 'none'
  }
}

/**
 * Get completed checklist items from work log form
 */
export function getCompletedChecklistItems(equipmentId) {
  const checkboxes = document.querySelectorAll('.checklist-checkbox:checked')
  
  if (checkboxes.length === 0) return null
  
  const completedItems = []
  checkboxes.forEach(cb => {
    completedItems.push(cb.dataset.itemId)
  })
  
  // Get checklist ID from the section
  const checklistSection = document.getElementById('checklistSection')
  if (!checklistSection) return null
  
  return {
    equipment_id: equipmentId,
    completed_items: completedItems
  }
}

// ==================== EVENT LISTENERS ====================

// Инициализируем сразу при загрузке модуля
initEventListeners();

function initEventListeners() {
  const addItemBtn = document.getElementById('addChecklistItemBtn')
  if (addItemBtn) {
    addItemBtn.onclick = (e) => {
      e.stopImmediatePropagation()
      addChecklistItemRow()
    }
  }

  const closeButtons = ['cancelChecklistBtn', 'closeChecklistModal']
  closeButtons.forEach(id => {
    const btn = document.getElementById(id)
    if (btn) btn.onclick = closeChecklistModal
  })
}
  


